import { useMemo, useRef } from 'react';
import type { ChatMessageItem } from '../../../types';

export interface AnchorItem {
  /** message id，作为 anchor 唯一标识 */
  id: string;
  /** 在原始 messages 数组里的下标，跳转用 */
  messageIndex: number;
  role: 'user' | 'assistant';
  /** user：原始 message 文本；assistant：固定走 i18n 文案 */
  label: string;
}

interface UseChatAnchorsOptions {
  assistantOutputLabel: string;
}

/** 拼出 message 内 anchor data attribute 的 CSS selector，供 DOM 滚动定位（ShareChat / Chat fineTune）用。 */
export const buildAnchorSelector = (id: string, role: AnchorItem['role']) =>
  `[data-anchor-id="${CSS.escape(id)}"][data-anchor-role="${role}"]`;

/**
 * 从 messages 派生锚点列表（user 全量 + 有 fileList 的 assistant）。
 *
 * 流式期间 messages 数组引用每帧都新（assistant 流式追加），但 anchors 维度真正变化的只有：
 *   - 长度（增/删消息）
 *   - 末位消息身份（id/role）
 *   - 末位 assistant 的 fileList 长度（产物追加会让最后一项从普通 assistant 升格为锚点）
 *   - i18n 文案
 *
 * 用这些做 O(1) 指纹，命中即返回旧引用，让下游 memo 组件保持稳定。
 *
 * 假设：user 消息一旦发出 content 就不再变化（claw 当前没有编辑 user 消息的能力）。
 * 若未来支持编辑，需要把 user.content 也纳入指纹或在指纹失效路径上单独处理。
 */
export const useChatAnchors = (
  messages: ChatMessageItem[],
  { assistantOutputLabel }: UseChatAnchorsOptions,
): AnchorItem[] => {
  const fingerprintRef = useRef<string>('');
  const anchorsRef = useRef<AnchorItem[]>([]);

  return useMemo(() => {
    const last = messages[messages.length - 1];
    const fingerprint = [
      messages.length,
      last?.id ?? '',
      last?.role ?? '',
      last?.role === 'assistant' ? (last.fileList?.length ?? 0) : 0,
      assistantOutputLabel,
    ].join('#');

    if (fingerprint === fingerprintRef.current) {
      return anchorsRef.current;
    }

    const items: AnchorItem[] = [];
    messages.forEach((m, idx) => {
      if (m.role === 'user') {
        items.push({ id: m.id, messageIndex: idx, role: 'user', label: m.content ?? '' });
      } else if (m.role === 'assistant' && (m.fileList?.length ?? 0) > 0) {
        items.push({ id: m.id, messageIndex: idx, role: 'assistant', label: assistantOutputLabel });
      }
    });

    fingerprintRef.current = fingerprint;
    anchorsRef.current = items;
    return items;
  }, [messages, assistantOutputLabel]);
};
